import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Spinner, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FaUser, FaEdit, FaEnvelope, FaPhone, FaMapMarkerAlt, FaCalendarAlt } from 'react-icons/fa';
import { getUserProfile } from '../services/userService';
import { useAuth } from '../context/AuthContext';

const ProfilePage = () => {
    const { currentUser } = useAuth();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                setLoading(true);
                const response = await getUserProfile();

                if (response.success) {
                    setProfile(response.user);

                    // Bu satırdan sonra ekle:
                    console.log('🔍 ProfilePage - API Response:', response);
                    console.log('🖼️ ProfilePage - Profile Image URL:', response.user?.profileImage);
                    console.log('📦 ProfilePage - Full Profile:', response.user);

                } else {
                    setError('Profil bilgileri alınamadı');
                }
            } catch (error) {
                console.error('Profil getirme hatası:', error);
                setError('Profil bilgileri yüklenirken bir hata oluştu');
            } finally {
                setLoading(false);
            }
        };

        fetchProfile();
    }, [currentUser]);

    const getOptimizedProfileImage = (url, size = 200) => {
        if (!url) return '';

        if (url.includes('cloudinary.com')) {
            try {
                const parts = url.split('/upload/');
                return `${parts[0]}/upload/w_${size},h_${size},c_fill,g_face/${parts[1]}`;
            } catch (e) {
                return url;
            }
        }

        return url;
    };

    if (loading) {
        return (
            <div className="profile-page">
                <section className="py-5">
                    <Container>
                        <div className="text-center">
                            <Spinner animation="border" role="status" style={{ color: 'var(--black)' }}>
                                <span className="visually-hidden">Yükleniyor...</span>
                            </Spinner>
                            <p className="mt-3">Profil bilgileri yükleniyor...</p>
                        </div>
                    </Container>
                </section>
            </div>
        );
    }

    if (error) {
        return (
            <div className="profile-page">
                <section className="py-5">
                    <Container>
                        <Alert variant="danger" className="text-center">
                            {error}
                        </Alert>
                    </Container>
                </section>
            </div>
        );
    }

    return (
        <div className="profile-page">
            {/* Header Section */}
            <section className="py-5">
                <Container>
                    <div className="text-center mb-5">
                        <h1 className="section-title">Profil Bilgileri</h1>
                        <p className="lead">Hesap bilgilerinizi görüntüleyin ve düzenleyin</p>
                    </div>
                </Container>
            </section>

            {/* Profile Content */}
            <section className="py-5 bg-light">
                <Container>
                    <Row className="g-4">
                        {/* Profile Image & Basic Info */}
                        <Col lg={4}>
                            <div className="modern-faq-item text-center">
                                <div className="profile-image-container mb-4">
                                    {profile?.profileImage ? (
                                        <img
                                            src={getOptimizedProfileImage(profile.profileImage)}
                                            alt="Profil Fotoğrafı"
                                            className="rounded-circle img-fluid"
                                            style={{
                                                width: '200px',
                                                height: '200px',
                                                objectFit: 'cover',
                                                border: '4px solid var(--white)',
                                                boxShadow: 'var(--shadow)',
                                                margin: '0 auto'
                                            }}
                                        />
                                    ) : (
                                        <div
                                            className="default-profile-image rounded-circle d-flex align-items-center justify-content-center mx-auto"
                                            style={{
                                                width: '200px',
                                                height: '200px',
                                                backgroundColor: 'var(--gray-100)',
                                                border: '4px solid var(--white)',
                                                boxShadow: 'var(--shadow)'
                                            }}
                                        >
                                            <FaUser style={{ fontSize: '5rem', color: 'var(--gray-400)' }} />
                                        </div>
                                    )}
                                </div>

                                <h3 className="modern-faq-question mb-2">
                                    {profile?.name || 'İsimsiz Kullanıcı'}
                                </h3>

                                {profile?.email && (
                                    <p className="text-muted mb-4">
                                        <FaEnvelope className="me-2" />
                                        {profile.email}
                                    </p>
                                )}

                                <Link to="/profile/edit">
                                    <Button className="apply-btn-black">
                                        <FaEdit className="me-2" />
                                        Profili Düzenle
                                    </Button>
                                </Link>
                            </div>
                        </Col>

                        {/* Profile Details */}
                        <Col lg={8}>
                            <div className="modern-faq-item">
                                <h3 className="modern-faq-question mb-4">Kişisel Bilgiler</h3>

                                <div className="modern-faq-answer">
                                    <Row className="g-4">
                                        <Col md={6}>
                                            <div className="modern-advantage-card">
                                                <div className="advantage-icon-modern">
                                                    <FaUser />
                                                </div>
                                                <div className="advantage-content">
                                                    <h5 className="advantage-title-modern">Ad Soyad</h5>
                                                    <p className="advantage-description-modern">
                                                        {profile?.name || 'Belirtilmemiş'}
                                                    </p>
                                                </div>
                                            </div>
                                        </Col>

                                        <Col md={6}>
                                            <div className="modern-advantage-card">
                                                <div className="advantage-icon-modern">
                                                    <FaEnvelope />
                                                </div>
                                                <div className="advantage-content">
                                                    <h5 className="advantage-title-modern">E-posta</h5>
                                                    <p className="advantage-description-modern">
                                                        {profile?.email || 'Belirtilmemiş'}
                                                    </p>
                                                </div>
                                            </div>
                                        </Col>

                                        <Col md={6}>
                                            <div className="modern-advantage-card">
                                                <div className="advantage-icon-modern">
                                                    <FaPhone />
                                                </div>
                                                <div className="advantage-content">
                                                    <h5 className="advantage-title-modern">Telefon</h5>
                                                    <p className="advantage-description-modern">
                                                        {profile?.phone || 'Belirtilmemiş'}
                                                    </p>
                                                </div>
                                            </div>
                                        </Col>

                                        <Col md={6}>
                                            <div className="modern-advantage-card">
                                                <div className="advantage-icon-modern">
                                                    <FaCalendarAlt />
                                                </div>
                                                <div className="advantage-content">
                                                    <h5 className="advantage-title-modern">Kayıt Tarihi</h5>
                                                    <p className="advantage-description-modern">
                                                        {profile?.createdAt
                                                            ? new Date(profile.createdAt).toLocaleDateString('tr-TR')
                                                            : 'Belirtilmemiş'
                                                        }
                                                    </p>
                                                </div>
                                            </div>
                                        </Col>

                                        <Col md={12}>
                                            <div className="modern-advantage-card">
                                                <div className="advantage-icon-modern">
                                                    <FaMapMarkerAlt />
                                                </div>
                                                <div className="advantage-content">
                                                    <h5 className="advantage-title-modern">Adres</h5>
                                                    <p className="advantage-description-modern">
                                                        {profile?.address || 'Belirtilmemiş'}
                                                    </p>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>
        </div>
    );
};

export default ProfilePage;